import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { WarehouseProductCardListProps } from './types';
import {
  ProductsPlaceholderWrapper,
  StyledListFooter,
  StyledListProgress,
  StyledListSentinel,
  StyledWarehouseProductCardList,
  StyledWarehouseProductCardListItem,
} from './styles';
import WarehouseProductCard from '../WarehouseProductCard';
import { StyledBox } from '../../styles/Grid/styles';
import Spinner from '../../atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../atoms/Spinner/enums';
import Placeholder from '../Placeholder';
import Button from '../../atoms/Button';
import { ButtonKind } from '../../atoms/Button/enums';

// Start fetching the next page ~300px before the sentinel enters the viewport.
const PREFETCH_ROOT_MARGIN = '300px 0px';

const supportsIntersectionObserver =
  typeof IntersectionObserver !== 'undefined';

const WarehouseProductCardList: React.FC<WarehouseProductCardListProps> = ({
  items,
  isHorizontal,
  hasMore,
  fetchMoreData,
  refetch,
  isFetchingMore = false,
  loadedCount,
  total,
}) => {
  const intl = useIntl();
  const noData = items.length === 0;
  const itemsCount = items.length;
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Mirrors of async state so the observer callback never reads stale props,
  // and a synchronous in-flight latch to stop double-fires before the
  // isFetchingMore prop has flipped true.
  const fetchingRef = useRef(isFetchingMore);
  const inFlightRef = useRef(false);

  useEffect(() => {
    fetchingRef.current = isFetchingMore;
    if (!isFetchingMore) {
      inFlightRef.current = false;
    }
  }, [isFetchingMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (
      !supportsIntersectionObserver ||
      !node ||
      !hasMore ||
      itemsCount === 0
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting);
        if (isIntersecting && !fetchingRef.current && !inFlightRef.current) {
          inFlightRef.current = true;
          fetchMoreData();
        }
      },
      { rootMargin: PREFETCH_ROOT_MARGIN }
    );

    observer.observe(node);
    return () => observer.disconnect();
    // itemsCount dep re-arms the observer after each page lands, so loading
    // chains until the sentinel leaves the prefetch margin (this is also what
    // fills viewports taller than the first page).
  }, [hasMore, fetchMoreData, itemsCount]);

  const showProgress =
    typeof total === 'number' && total > 0 && typeof loadedCount === 'number';

  return (
    <>
      {noData ? (
        <ProductsPlaceholderWrapper>
          <Placeholder
            title={intl.formatMessage({
              id: 'warehouse.noResults',
              defaultMessage: 'No results',
            })}
          />
        </ProductsPlaceholderWrapper>
      ) : null}

      {isHorizontal && items.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              '56px minmax(0, 2fr) 72px 80px 80px 96px 100px auto',
            columnGap: '1.25rem',
            padding: '0 0.75rem',
            marginBottom: '0.375rem',
            color: 'var(--muted)',
            fontSize: 'var(--t-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <span style={{ gridColumn: 'span 2' }}>Product</span>
          <span>Ordered</span>
          <span>Remaining</span>
          <span>Damaged</span>
          <span>Created</span>
          <span>Price</span>
          <div />
        </div>
      )}
      <StyledWarehouseProductCardList {...{ isHorizontal }}>
        {items.map((item) => (
          <StyledWarehouseProductCardListItem
            {...{ key: item.productId, isHorizontal }}
          >
            <WarehouseProductCard
              {...{ isHorizontal, ...item }}
              refetchProducts={refetch}
            />
          </StyledWarehouseProductCardListItem>
        ))}
      </StyledWarehouseProductCardList>

      {!noData && (
        <StyledListFooter>
          <StyledListSentinel ref={sentinelRef} aria-hidden='true' />
          {isFetchingMore && (
            <StyledBox
              role='status'
              justifyContent='center'
              alignItems='center'
              gap='0.625rem'
            >
              <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.ExtraSmall} />
              <StyledListProgress>
                {intl.formatMessage({
                  id: 'warehouse.loadingMore',
                  defaultMessage: 'Loading more products…',
                })}
              </StyledListProgress>
            </StyledBox>
          )}
          {!supportsIntersectionObserver && hasMore && !isFetchingMore && (
            <Button kind={ButtonKind.Outline} onClick={fetchMoreData}>
              {intl.formatMessage({
                id: 'warehouse.loadMore',
                defaultMessage: 'Load more',
              })}
            </Button>
          )}
          {showProgress && (
            <StyledListProgress>
              {intl.formatMessage(
                {
                  id: 'warehouse.loadedOfTotal',
                  defaultMessage: 'Loaded {loaded} of {total} products',
                },
                {
                  loaded: (loadedCount as number).toLocaleString(),
                  total: (total as number).toLocaleString(),
                }
              )}
            </StyledListProgress>
          )}
        </StyledListFooter>
      )}
    </>
  );
};

export default WarehouseProductCardList;
